import { MainArea, Page } from '@salecycle/ui-component-library';
import { FC, useEffect } from 'react';
import { useQuery } from 'react-query';
import * as RestApi from 'core/network/RestApi';
import { useParams, generatePath, useLocation } from 'react-router-dom';
import { Sidebar } from './editor/Sidebar';
import {
  useOffsiteCampaignEditorDispatch,
  useOffsiteCampaignEditorState,
} from '../context/UseOffsiteCampaignEditorContext';
import AppLoader from 'core/ui/components/AppLoader';
import { Toolbar } from './editor/Toolbar';
import { ITemplate } from 'entities/template/template';
import SegmentList from './editor/SegmentList';
import { getProgress } from './progress';
import { IOffsiteCampaign, removeDanglingSplitTestIds } from '../../entities/offsiteCampaign';
import { useTranslation } from 'react-i18next';
import { Site } from 'sections/client-management/entities';
import { OffsiteCampaignEndpoints } from '../../constants/api-endpoints';
import { StyledEditor, StyledGrid } from './styles';
import { uuid } from 'utils';
import { useFeatureFlags } from 'core/app/AppContext';
import {
  IntegrationChannel,
  IntegrationResponse,
} from 'sections/client-management/ui/pages/client-settings/integrations/context/IntegrationsState';
import {
  DispatcherIntegrationApiRow,
  normalizeDispatcherIntegrationResponse,
} from 'sections/client-management/ui/pages/client-settings/integrations/data/IntegrationsApi';

export const OffsiteCampaignEditor: FC = () => {
  const { t } = useTranslation('campaigns');
  const dispatch = useOffsiteCampaignEditorDispatch();
  const { siteId, campaignId } = useParams<{ siteId: string; campaignId: string }>();
  const {
    campaign,
    originalCampaign,
    hasUnsavedCampaignChange,
    hasOrderChange,
    site,
  } = useOffsiteCampaignEditorState();
  const location = useLocation<{ showAddNewSegmentModal: boolean; addDefaultSegment: boolean }>();
  const siteQuery = useQuery(['site', siteId], async () => {
    const result = await RestApi.get<Site>(generatePath(OffsiteCampaignEndpoints.GET_SITE, { siteId }));
    return result.data;
  });

  const { flagEnabled } = useFeatureFlags();
  const featureSplitTest = flagEnabled('ld-split-test-scheduling');

  const dispatchersQuery = useQuery(['dispatchers', siteId], async () => {
    const result = await RestApi.get<DispatcherIntegrationApiRow[]>(`/sites/${siteId}/configs`);
    return (result.data ?? [])
      .map(row => normalizeDispatcherIntegrationResponse(row))
      .filter((row): row is IntegrationResponse => row != null);
  });

  const offsiteCampaignQuery = useQuery(['offsite-campaign', siteId, campaignId], async () => {
    const result = await RestApi.get<IOffsiteCampaign>(
      generatePath(OffsiteCampaignEndpoints.OFFSITE_GET_BY_ID, { siteId, campaignId }),
      undefined,
      featureSplitTest ? { 'Api-Version': 'v2' } : undefined,
    );
    if (featureSplitTest) {
      removeDanglingSplitTestIds(result.data);
    }
    return result.data;
  });

  const templatesQuery = useQuery(['templates', siteId, campaignId], async () => {
    const result = await RestApi.get<ITemplate[]>(`/sites/${siteId}/templates/new`);
    return result.data;
  });

  const addDefaultSegment =
    location.state === undefined || location.state.addDefaultSegment === undefined
      ? false
      : location.state.addDefaultSegment;

  // Reset initial state
  useEffect(() => () => dispatch({ type: 'RESET_EDITOR' }), [dispatch]);

  // Initial site load
  useEffect(() => {
    if (!siteQuery.isLoading) {
      const loadedSite: Site = siteQuery?.data ?? null;
      dispatch({
        type: 'SET_SITE_INITIAL',
        payload: { site: loadedSite },
      });
    }
  }, [siteQuery.isLoading, dispatch, siteQuery.data]);

  // Load available channels for current site from dispatcher config
  useEffect(() => {
    if (!dispatchersQuery.isLoading && dispatchersQuery.data?.length) {
      // we want to retrieve a single channel, so we use set to avoid duplicates
      const channelsSet = new Set(
        dispatchersQuery.data.reduce((acc, d) => {
          // we skip migration channel because it's not meaningful
          if (d.channel !== IntegrationChannel.MIGRATION) {
            if (d.channel === IntegrationChannel.WHATSAPP) {
              flagEnabled('ld-wax') && acc.push(d.channel);
            } else if (d.channel === IntegrationChannel.RCS) {
              flagEnabled('ld-rcs') && acc.push(d.channel);
            } else {
              acc.push(d.channel);
            }
          }
          return acc;
        }, []),
      );
      dispatch({
        type: 'SET_ACTIVE_CHANNELS',
        payload: Array.from(channelsSet).sort(),
      });
    }
    // TODO disabling eslint for the deps since we overflow the stack if we pass in the flagEnabled. Can be removed once the flag is gone
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchersQuery.isLoading, dispatch, dispatchersQuery.data]);

  // Initial campaign values
  useEffect(() => {
    if (!offsiteCampaignQuery.isLoading) {
      const loadedCampaign: IOffsiteCampaign = offsiteCampaignQuery?.data ?? null;
      if (addDefaultSegment) {
        loadedCampaign.segments.push({
          id: uuid(),
          name: t('campaigns:offsiteEditor.segments.segmentName.defaultName'),
          type: 'all',
          segmentFilters: [],
          percentageSplits: [
            {
              id: uuid(),
              percentage: 100,
              name: '',
              cycles: [
                {
                  templateId: null,
                  messageType: 'email',
                  delay: 0,
                  id: uuid(),
                  subjectLineId: undefined,
                },
              ],
            },
          ],
        });
      }
      dispatch({
        type: 'SET_CAMPAIGN_INITIAL',
        payload: { campaign: loadedCampaign },
      });
    }
  }, [campaignId, offsiteCampaignQuery.isLoading, dispatch, offsiteCampaignQuery.data, addDefaultSegment, t]);

  // Load templates
  useEffect(() => {
    if (!templatesQuery.isLoading) {
      const templates: ITemplate[] = templatesQuery?.data ?? null;
      dispatch({
        type: 'SET_TEMPLATES',
        payload: { templates },
      });
    }
  }, [campaignId, templatesQuery.isLoading, dispatch, templatesQuery.data]);

  // Check for changes to the campaign
  useEffect(() => {
    if (JSON.stringify(campaign) !== JSON.stringify(originalCampaign)) {
      dispatch({
        type: 'SET_UNSAVED_CAMPAIGN_CHANGES',
        payload: { hasUnsavedCampaignChange: true },
      });
    } else {
      dispatch({
        type: 'SET_UNSAVED_CAMPAIGN_CHANGES',
        payload: { hasUnsavedCampaignChange: false },
      });
    }
    dispatch({
      type: 'SET_HAS_MISSING_FIELDS',
      payload: {
        hasMissingFields: !!campaign.segments.find(
          segment => getProgress(segment, site.sessionTime, site.unsubscribeSettings, campaign.type, t).percent < 100,
        ),
      },
    });
  }, [
    hasUnsavedCampaignChange,
    dispatch,
    site.sessionTime,
    hasOrderChange,
    site.unsubscribeSettings,
    t,
    campaign,
    originalCampaign,
  ]);

  if (offsiteCampaignQuery.isLoading || templatesQuery.isLoading || siteQuery.isLoading || !site || !campaign) {
    return (
      <StyledEditor>
        <AppLoader showGradient={true} />
      </StyledEditor>
    );
  }

  return (
    <>
      <StyledEditor>
        <MainArea>
          <Page padding={0}>
            <StyledGrid>
              <Sidebar />
              <div>
                <SegmentList />
              </div>
            </StyledGrid>
          </Page>
          <Toolbar />
        </MainArea>
      </StyledEditor>
    </>
  );
};

export default OffsiteCampaignEditor;
